import type { IncomeEmploymentTabProps } from "../../../types/tabs";
import { useIncomeFilePreview } from "./hooks/useIncomeFilePreview";
import { useVerifiedIncome } from "./hooks/useVerifiedIncome";
import { useIncomeTabBankLinking } from "./hooks/useIncomeTabBankLinking";
import { useIncomeDocuments } from "./hooks/useIncomeDocuments";
import { useEmploymentFormState } from "./hooks/useEmploymentFormState";
import type { IncomeAttachmentFile } from "../../../types/incomeEmploymentApi";
import type {
  IncomeEmploymentEditViewProps,
  IncomeEmploymentReadOnlyViewProps,
} from "../../../types/incomeEmployment";

export function useIncomeEmploymentTab({
  applicationAllData,
  applicationData,
  applicantId,
  isEdit = false,
  onFormChange,
  exposeIncomeInlineValidation = false,
  onRefresh,
}: IncomeEmploymentTabProps) {
  const { handleApplicationIncomeFilePreview } =
    useIncomeFilePreview(applicantId);

  const verifiedIncome = useVerifiedIncome({
    applicantId,
    applicationId: applicationAllData?.application_id,
    applicationData: applicationData ?? {},
    embeddedNotes: applicationAllData?.notes,
    onRefresh,
  });

  const bankLinking = useIncomeTabBankLinking({
    applicantId,
  });

  const documents = useIncomeDocuments({
    applicantId,
    applicationData: (applicationData ?? {}) as unknown as {
      applicant_status?: string;
      bank_name?: string;
      ninja_income_proof?: IncomeAttachmentFile[];
      proof_of_income?: IncomeAttachmentFile[];
    },
    onRefresh,
  });

  const employment = useEmploymentFormState({
    applicationData: applicationData ?? {},
    applicationAllData,
    applicantId,
    isEdit,
    onFormChange,
    onRefresh,
    exposeIncomeInlineValidation: exposeIncomeInlineValidation ?? false,
  });

  const shared = {
    applicantId,
    applicationData,
    applicationAllData,
    onRefresh,
    currentJobs: employment.currentJobs,
    previousJobs: employment.previousJobs,
    otherIncomeData: employment.otherIncomeData,
    incomeVerificationDocuments: documents.incomeVerificationDocuments,
    handleApplicationIncomeFilePreview,
    verifiedIncome,
    bankLinking,
  };

  const editViewProps: IncomeEmploymentEditViewProps = {
    ...shared,
    employment,
    documents,
  };

  const readOnlyViewProps: IncomeEmploymentReadOnlyViewProps = {
    ...shared,
  };

  return {
    isEdit,
    editViewProps,
    readOnlyViewProps,
  };
}

export type IncomeEmploymentTabContext = ReturnType<typeof useIncomeEmploymentTab>;
